// src/components/CampaignAnalytics.tsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Button } from "@/components/ui/button";
import { Download, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PerformanceData {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roi: number;
  frequency: number;
}

interface Campaign {
  id: string;
  name: string;
  metrics: {
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    ctr: number;
    cpc: number;
    cpa: number;
    budget?: number;
    roi?: number;
    frequency?: number;
  };
  dateRange: {
    start: string;
    end: string;
  };
  platform: 'facebook' | 'google' | 'tiktok';
}

interface CampaignAnalyticsProps {
  campaigns: Campaign[];
  dateRange: { from: Date; to: Date };
  onExport: () => void;
}

const PERFORMANCE_THRESHOLDS = {
  roi: { good: 2, bad: 1 },
  ctr: { good: 2, bad: 1 },
  frequency: { good: 3, bad: 5 }
};

const calculatePerformanceScore = (campaign: Campaign): number => {
  let score = 0;
  const metrics = campaign.metrics;

  // ROI Score
  if (metrics.roi) {
    if (metrics.roi >= PERFORMANCE_THRESHOLDS.roi.good) score += 3;
    else if (metrics.roi >= PERFORMANCE_THRESHOLDS.roi.bad) score += 1;
  }

  // CTR Score
  if (metrics.ctr >= PERFORMANCE_THRESHOLDS.ctr.good) score += 3;
  else if (metrics.ctr >= PERFORMANCE_THRESHOLDS.ctr.bad) score += 1;

  // Frequency Score
  if (metrics.frequency && metrics.frequency <= PERFORMANCE_THRESHOLDS.frequency.good) score += 3;
  else if (metrics.frequency && metrics.frequency <= PERFORMANCE_THRESHOLDS.frequency.bad) score += 1;

  return score;
};

const getPerformanceInsights = (campaigns: Campaign[]): string[] => {
  const insights: string[] = [];
  
  // Best performing campaign
  const bestCampaign = [...campaigns].sort((a, b) => calculatePerformanceScore(b) - calculatePerformanceScore(a))[0];
  if (bestCampaign) {
    insights.push(`A campanha "${bestCampaign.name}" está com o melhor desempenho geral.`);
  }

  // High CPA alerts
  const highCPACampaigns = campaigns.filter(c => c.metrics.cpa > 50);
  if (highCPACampaigns.length > 0) {
    insights.push(`${highCPACampaigns.length} campanha(s) com CPA elevado necessitam atenção.`);
  }

  // Budget utilization
  const underUtilizedCampaigns = campaigns.filter(c => 
    c.metrics.budget && c.metrics.spend < c.metrics.budget * 0.7
  );
  if (underUtilizedCampaigns.length > 0) {
    insights.push(`${underUtilizedCampaigns.length} campanha(s) estão com utilização de orçamento abaixo de 70%.`);
  }

  return insights;
};

export const CampaignAnalytics: React.FC<CampaignAnalyticsProps> = ({ campaigns, dateRange, onExport }) => {
  // Aggregate daily data
  const dailyData: PerformanceData[] = [];
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  let currentDate = startDate;

  while (currentDate <= endDate) {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const dayStats = {
      date: format(currentDate, 'dd/MM', { locale: ptBR }),
      spend: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      ctr: 0,
      cpc: 0,
      cpa: 0,
      roi: 0,
      frequency: 0
    };

    campaigns.forEach(campaign => {
      if (campaign.dateRange.start <= dateStr && campaign.dateRange.end >= dateStr) {
        const metrics = campaign.metrics;
        dayStats.spend += metrics.spend;
        dayStats.impressions += metrics.impressions;
        dayStats.clicks += metrics.clicks;
        dayStats.conversions += metrics.conversions;
      }
    });

    // Calculate derived metrics
    dayStats.ctr = (dayStats.clicks / dayStats.impressions) * 100;
    dayStats.cpc = dayStats.clicks > 0 ? dayStats.spend / dayStats.clicks : 0;
    dayStats.cpa = dayStats.conversions > 0 ? dayStats.spend / dayStats.conversions : 0;
    dayStats.roi = dayStats.spend > 0 ? (dayStats.conversions * 100) / dayStats.spend : 0;
    dayStats.frequency = dayStats.impressions / (dayStats.impressions * 0.8); // Estimated reach

    dailyData.push(dayStats);
    currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
  }

  return (
    <div className="space-y-6 bg-white dark:bg-gray-900">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Performance Trends Chart */}
        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Performance Trends</CardTitle>
            <CardDescription className="text-gray-500 dark:text-gray-400">Análise de tendências dos principais indicadores</CardDescription>
          </CardHeader>
          <CardContent className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="spend"
                  name="Spend"
                  stroke="#FF3366"
                  fill="#FF3366"
                  fillOpacity={0.1}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="conversions"
                  name="Conversions"
                  stroke="#36D399"
                  fill="#36D399"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance Heatmap */}
        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-100">Performance Heatmap</CardTitle>
            <CardDescription className="text-gray-500 dark:text-gray-400">Distribuição de performance por métrica</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {campaigns.map(campaign => (
                <div
                  key={campaign.id}
                  className={`p-4 rounded-lg ${
                    calculatePerformanceScore(campaign) >= 7
                      ? 'bg-green-100 dark:bg-green-800'
                      : calculatePerformanceScore(campaign) >= 4
                      ? 'bg-yellow-100 dark:bg-yellow-800'
                      : 'bg-red-100 dark:bg-red-800'
                  }`}
                >
                  <p className="text-sm font-medium truncate text-gray-900 dark:text-gray-100" title={campaign.name}>
                    {campaign.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Score: {calculatePerformanceScore(campaign)}/9
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card className="border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-gray-100">Performance Insights</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-400">Recomendações baseadas em análise de dados</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {getPerformanceInsights(campaigns).map((insight, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              >
                {insight.includes("melhor") ? (
                  <TrendingUp className="w-5 h-5 text-green-500 mt-0.5" />
                ) : insight.includes("atenção") ? (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500 mt-0.5" />
                )}
                <p className="text-gray-900 dark:text-gray-100">{insight}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card className="border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-gray-100">Exportar Análise</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-400">Baixe relatórios detalhados de performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button onClick={onExport} className="bg-[#FF3366] text-white hover:bg-[#FF3366]/90">
              <Download className="w-4 h-4 mr-2" />
              Exportar Relatório Completo
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Download className="w-4 h-4 mr-2" />
              Imprimir Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};